import React from 'react';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import {
  Box, Grid, Button, FormControl, InputLabel,
  Select, MenuItem, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Autocomplete, CircularProgress,
  Typography, Divider, Paper, Chip
} from '@mui/material';
import { getRequest, putRequest, patchRequest, postRequest, deleteRequest } from 'Includes/api/apicall';
import { GET_URL, POST_URL } from 'Includes/urls';
import { Link } from 'react-router-dom';
import { AddCircleOutlineOutlined } from '@mui/icons-material';
import { loadBillingFilters } from 'Includes/billingFilterStorage';
import Swal from 'sweetalert2';

function CompanyBillingConfigList(props) {

  const storedFilters = loadBillingFilters();

  const [rows, setRows] = React.useState([]);
  const [rowCount, setRowCount] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [academicYearList, setAcademicYearList] = React.useState([]);
  const [selectedAcademicYear, setSelectedAcademicYear] = React.useState(storedFilters.academicYearId || '');
  const [inactiveFilter, setInactiveFilter] = React.useState('active');
  const [selectedCoordinator, setSelectedCoordinator] = React.useState(null);
  const [selectedRows, setSelectedRows] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(10);

  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [editingRow, setEditingRow] = React.useState(null);
  const [editStrength, setEditStrength] = React.useState('');
  const [editCoordinator, setEditCoordinator] = React.useState('');
  const [users, setUsers] = React.useState([]);
  const [editError, setEditError] = React.useState('');
  const [savingEdit, setSavingEdit] = React.useState(false);

  const [copyModalOpen, setCopyModalOpen] = React.useState(false);
  const [copyModalLoading, setCopyModalLoading] = React.useState(false);
  const [copyFormData, setCopyFormData] = React.useState([]);
  const [targetYear, setTargetYear] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  const columns = [
    { field: 'company_name', headerName: 'Company', flex: 1.2 },
    {
      field: 'inactive_dead_client',
      headerName: 'Status',
      width: 140,
      renderCell: (p) => (
        <Chip
          label={p.value ? 'Dead' : 'Active'}
          color={p.value ? 'error' : 'success'}
          size="small"
        />
      )
    },
    { field: 'coordinator_name', headerName: 'Coordinator', flex: 1 },
    { field: 'student_strength', headerName: 'Students', width: 140 },

    {
      field: 'actions',
      type: 'actions',
      getActions: (params) => [
        <GridActionsCellItem label="Edit" onClick={() => openEditDialog(params.row)} showInMenu />,
        <GridActionsCellItem label="Mark Dead" onClick={() => markDeadClient(params.row)} showInMenu />,
        <GridActionsCellItem label="Delete" onClick={() => handleDelete(params.row)} showInMenu />
      ]
    }
  ];

  React.useEffect(() => {
    getAcademicYearList();
    getUsers();
  }, []);

  const getAcademicYearList = () => {
    getRequest(GET_URL.academicyear.api, {}, props).then(res => {
      if (res?.status === 200) {
        const list = res.data.data || res.data || [];
        setAcademicYearList(list);
        if (selectedAcademicYear) {
          getConfigList(selectedAcademicYear);
        }
      }
    });
  };

  const getUsers = () => {
    getRequest(GET_URL.user.api, { groups: '4' }, props).then(res => {
      if (res?.status === 200) {
        setUsers(res.data.data || res.data || []);
      }
    });
  };

  const getConfigList = (year) => {
    setLoading(true);
    const params = {
      year,
      limit: pageSize,
      pageno: page + 1,
      ...(inactiveFilter === 'dead' ? { include_dead_client_only: 1 } : {}),
      ...(selectedCoordinator?.id ? { coordinator: selectedCoordinator.id } : {}),
    };
    getRequest(GET_URL.company_billing_config.api, params, props)
      .then(res => {
        setLoading(false);
        if (res?.status === 200) {
          const data = res.data.data || [];
          setRows(data);
          setRowCount(res.data?.count || data.length);
        }
      })
      .catch(() => setLoading(false));
  };

  const handleAcademicYearChange = (e) => {
    const year = e.target.value;
    setSelectedAcademicYear(year);
    setPage(0);
    if (year) getConfigList(year);
  };

  const handleCoordinatorChange = (_e, value) => {
    setSelectedCoordinator(value);
    setPage(0);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0);
  };

  React.useEffect(() => {
    if (selectedAcademicYear) {
      getConfigList(selectedAcademicYear);
    }
  }, [page, pageSize, inactiveFilter, selectedCoordinator]);

  const openEditDialog = (row) => {
    setEditingRow(row);
    setEditStrength(row.student_strength);
    setEditCoordinator(row.coordinator);
    setEditDialogOpen(true);
  };

  const handleEditClose = () => setEditDialogOpen(false);

  const handleEditSave = () => {
    if (!editStrength) return;
    setSavingEdit(true);

    putRequest(`${GET_URL.company_billing_config.api}${editingRow.id}/`, {
      student_strength: editStrength,
      coordinator: editCoordinator
    }, props).then(() => {
      setSavingEdit(false);
      handleEditClose();
      getConfigList(selectedAcademicYear);
    });
  };

  const markDeadClient = (row) => {
    patchRequest(`${GET_URL.company_billing_config.api}${row.id}/`, { inactive_dead_client: true }, props)
      .then(() => {
        setRows(prev => prev.filter(r => r.id !== row.id));
      });
  };

  const handleDelete = (row) => {
    deleteRequest(`${GET_URL.company_billing_config.api}${row.id}/`, {}, props)
      .then(() => {
        setRows(prev => prev.filter(r => r.id !== row.id));
      });
  };

  const handleOpenCopyModal = () => {
    setCopyModalOpen(true);
    setCopyFormData(rows.filter(r => selectedRows.includes(r.id)));
  };

  const handleCloseCopyModal = () => setCopyModalOpen(false);

  return (
    <Box sx={{ p: 3 }}>

      {/* HEADER */}
      <Grid container alignItems="center">
        <Grid item xs={6}>
          <Typography variant="h5" fontWeight={600}>Company Billing Configs</Typography>
        </Grid>

        <Grid item xs={6} textAlign="right">
          {selectedRows.length > 0 && (
            <Button variant="contained" onClick={handleOpenCopyModal} sx={{ mr: 1 }}>
              Copy ({selectedRows.length})
            </Button>
          )}
          <Button
            variant="contained"
            component={Link}
            to="/billing/company-billing-config"
            startIcon={<AddCircleOutlineOutlined />}
          >
            Create
          </Button>
        </Grid>
      </Grid>

      {/* FILTER */}
      <Paper sx={{ mt: 3, p: 2 }}>
        <Grid container spacing={2}>
          <Grid item md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Academic Year</InputLabel>
              <Select value={selectedAcademicYear} onChange={handleAcademicYearChange}>
                {academicYearList.map(y => (
                  <MenuItem key={y.id} value={y.id}>{y.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item md={4}>
            <Autocomplete
              options={users}
              getOptionLabel={(o) => o.username || ''}
              onChange={handleCoordinatorChange}
              renderInput={(params) => <TextField {...params} label="Coordinator" size="small" />}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* TABLE */}
      <Paper sx={{ mt: 3, p: 2 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          checkboxSelection
          pagination
          paginationMode="server"
          page={page}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          rowsPerPageOptions={[10, 20, 50]}
          rowCount={rowCount}
          autoHeight
          onSelectionModelChange={(s) => setSelectedRows(s)}
          selectionModel={selectedRows}
        />
      </Paper>

      {/* EDIT */}
      <Dialog open={editDialogOpen} onClose={handleEditClose}>
        <DialogTitle>Edit</DialogTitle>
        <DialogContent>
          <TextField
            label="Student Strength"
            fullWidth
            value={editStrength}
            onChange={(e) => setEditStrength(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose}>Cancel</Button>
          <Button onClick={handleEditSave}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* COPY MODAL */}
      <Dialog open={copyModalOpen} onClose={handleCloseCopyModal} maxWidth="md" fullWidth>
        <DialogTitle>Copy Configs</DialogTitle>
        <DialogContent>
          {copyFormData.map((item, i) => (
            <Box key={i} sx={{ mb: 2 }}>
              <Typography>{item.company_name}</Typography>
              <Divider sx={{ my: 1 }} />
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCopyModal}>Cancel</Button>
          <Button variant="contained">Submit</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}

export default CompanyBillingConfigList;